// features/workspace.js — WORKSPACE FEATURE (landing page)
// ═══════════════════════════════════════════════════════════
// Landing page for the new Workspace feature. Identical layout to
// the Space feature (ShiftPanelHeader + Sessions/Invites tabs + card
// grid) but:
//   - Filters sessions where is_workspace = true
//   - Adds a [Solo] button next to [+ Invite]
//   - Solo path: creates a workspace for the current user only
//   - Invite path: normal invite flow for multi-user workspaces (same
//     modal as Space uses, coexists without interfering)
//
// When the user clicks a session card, they enter the new interactive
// workstation UI (Layer 1 + Layer 2). That is built in Phase 2.
// For now, Phase 1 ships the landing page + Solo modal + create flow.
//
// Props (from pages/index.js):
//   onClose        — returns to chat view
//   isMobile       — layout hint
//   session        — Supabase auth session
//   onInvite       — opens the invite modal (same as Space's)
//   onSolo         — opens the Solo creation modal (new, Workspace-only)
//   spaceHook      — the useSpace hook with sessions/invites
//   defaultTab     — which tab to open ('Sessions' | 'Invites')

import { useState, useEffect } from 'react';
import ShiftPanelHeader from '../components/shift-panel-header';
import ShiftCard from '../components/shift-card';
import DeleteSpaceConfirm from '../components/delete-space-confirm';
import { useI18n } from '../hooks/use-i18n';
import { getSpaceType } from '../lib/space-types';

export default function Workspace({
  onClose, isMobile, session, onInvite, onSolo,
  spaceHook, defaultTab,
}) {
  const {
    sharedSessions, sharedSpaceLoading,
    spaceSearch, setSpaceSearch,
    openSpaceSession, deleteSpaceSession, leaveSpaceSession,
    loadSharedSpace,
    pendingInvites, sentInvites, sentInvitesLoading,
    loadSentInvites, acceptSharedInvite, declineSharedInvite, withdrawInvite,
  } = spaceHook;
  const { t } = useI18n();
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab || 'Sessions');

  // Refresh when switching tabs — ensures latest data
  useEffect(() => {
    loadSharedSpace?.();
    if (activeTab === 'Invites') loadSentInvites?.();
  }, [activeTab]);


  // Toolbar shown on Sessions tab — sits between tabs and content, never scrolls
  function SpaceToolbar({ activeTab }) {
    if (activeTab !== 'Sessions') return null;
    return (
      <div className="cards-toolbar" style={{ borderBottom: '1px solid var(--border-1)', background: 'var(--surface-1)', flexShrink: 0 }}>
        <div className="cards-search-wrap">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="5" stroke="var(--text-5)" strokeWidth="1.4"/>
            <path d="M10.5 10.5L14 14" stroke="var(--text-5)" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="cards-search"
            placeholder="Search sessions…"
            value={spaceSearch}
            onChange={e => setSpaceSearch(e.target.value)}
            onFocus={e => e.target.style.borderColor = 'var(--gold)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-2)'}
          />
        </div>
      </div>
    );
  }

  // Sessions tab content
  function SessionsTab() {
    // Show only Workspace sessions (is_workspace = true). Includes Solo
    // workspaces (1 participant = just the creator) AND multi-user
    // workspaces after invitees have joined. The Space feature continues
    // to filter by participants >= 2 for its own landing page.
    //
    // EXCLUDE project-backed workspaces — those are surfaced through the
    // Projects panel, not here. Each Project owns a private workspace
    // tagged workspace_metadata.mode === 'project' that should only ever
    // open from the Projects card click.
    const activeSessions = sharedSessions.filter(s =>
      s.is_workspace === true &&
      s.workspace_metadata?.mode !== 'project' &&
      (s.participants || []).length >= 1
    );
    const filtered = activeSessions.filter(s => {
      if (!spaceSearch.trim()) return true;
      const q = spaceSearch.toLowerCase();
      const others = (s.participants || [])
        .filter(p => p !== session?.user?.id)
        .join(', ')
        .toLowerCase();
      return others.includes(q) || (s.what_shifted || '').toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q);
    });

    if (sharedSpaceLoading) {
      return (
        <div style={{ color: 'var(--text-7)', fontSize: '13px', fontStyle: 'italic' }}>
          loading···
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', paddingTop: '60px', gap: '18px',
        }}>
          <svg width="38" height="38" viewBox="0 0 40 40" fill="none" opacity="0.2">
            <circle cx="26" cy="13" r="6" stroke="var(--text-1)" strokeWidth="1.5"/>
            <path d="M19 32c0-5 3.6-9 7-10a10 10 0 019 10" stroke="var(--text-1)" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="15" cy="13" r="7" stroke="var(--text-1)" strokeWidth="1.6"/>
            <path d="M4 32c0-5.5 4.5-10 11-10s11 4.5 11 10" stroke="var(--text-1)" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <div style={{
            color: 'var(--text-7)', fontSize: '13px', lineHeight: '1.8',
            fontStyle: 'italic', textAlign: 'center', maxWidth: '380px',
          }}>
            {spaceSearch.trim()
              ? `No sessions match "${spaceSearch}"`
              : t('space.no_sessions')
            }
          </div>
          {!spaceSearch.trim() && (
            <button
              onClick={onInvite}
              style={{
                background: 'none', border: '1px solid var(--border-1)',
                borderRadius: 'var(--radius-md)', color: 'var(--text-5)',
                fontSize: '12px', fontFamily: 'var(--font-serif)',
                letterSpacing: '0.08em', padding: '8px 20px', cursor: 'pointer',
                marginTop: '4px', transition: 'all var(--transition-base)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.color = 'var(--teal)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-1)'; e.currentTarget.style.color = 'var(--text-5)'; }}
            >
              {t('space.invite_button')}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="cards-grid">
        {filtered.map(s => {
          const others = s.others || (s.participants || []).filter(p => p !== session?.user?.id);
          // Solo workspaces carry space_type='space' for FK compat — but the
          // truth signal is workspace_metadata.solo === true. Solo wins first
          // so the badge reads SOLO, not SPACE.
          const isSolo = s.workspace_metadata?.solo === true;
          const { label: typeLabel, color: typeColor } = isSolo
            ? { label: 'SOLO', color: '#c8a96e' }
            : getSpaceType(s.space_type);
          const isCreator = s.created_by === session?.user?.id;
          const snippet = s.lastMessage || s.what_shifted
            || (isCreator && others.length === 0 ? 'waiting for them to join' : 'no messages yet');

          // Map space session → ShiftCard item shape
          const cardItem = {
            id:           s.id,
            title:        s.name || (others.length > 0
                            ? `with ${others.map(o => s.otherNames?.[o] || 'someone').join(', ')}`
                            : 'Shared space'),
            created_at:   s.created_at,
            last_used_at: s.updated_at || null,
            summary_bullets: [],
          };

          return (
            <ShiftCard
              key={s.id}
              item={cardItem}
              accentColor={typeColor}
              typeLabel={typeLabel}
              preview={snippet}
              actionLabel="Open →"
              onAction={() => { openSpaceSession(s); }}
              onDelete={() => setConfirmAction({ spaceId: s.id, spaceName: cardItem.title, isCreator: s.created_by === session?.user?.id })}
            />
          );
        })}
      </div>
    );
  }

  // Invites tab content
  function InvitesTab() {
    // null = not yet fetched — show loader
    if (sentInvitesLoading || sentInvites === null) {
      return (
        <div style={{ color: 'var(--text-7)', fontSize: '13px', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          loading···
        </div>
      );
    }

    // Only show pending invites — hide accepted/declined/expired
    const allInvites = [
      ...pendingInvites.map(i => ({ ...i, direction: 'received' })),
      ...(sentInvites || []).filter(i => i.status === 'pending').map(i => ({ ...i, direction: 'sent' })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (allInvites.length === 0) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', paddingTop: '80px', gap: '16px',
        }}>
          <svg width="32" height="32" viewBox="0 0 36 36" fill="none" opacity="0.18">
            <circle cx="14" cy="14" r="8" stroke="var(--text-1)" strokeWidth="1.4"/>
            <path d="M14 7v7l4 4" stroke="var(--text-1)" strokeWidth="1.3" strokeLinecap="round"/>
            <path d="M24 28l6-6M30 28l-6-6" stroke="var(--text-1)" strokeWidth="1.3" strokeLinecap="round"/>
            <circle cx="27" cy="25" r="6" stroke="var(--text-1)" strokeWidth="1.3"/>
            <path d="M27 22v3l2 2" stroke="var(--text-1)" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          <div style={{ color: 'var(--text-5)', fontSize: '14px', fontStyle: 'italic', fontFamily: 'var(--font-serif)', textAlign: 'center' }}>
            No invites yet.
          </div>
          <button
            onClick={onInvite}
            style={{
              background: 'var(--green-bg)', border: '1px solid var(--green)',
              borderRadius: 'var(--radius-md)', color: 'var(--green)',
              fontSize: '13px', fontFamily: 'var(--font-serif)',
              letterSpacing: '0.06em', padding: '9px 22px', cursor: 'pointer',
              transition: 'background var(--transition-base)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--green-border)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--green-bg)'}
          >
            {t('space.invite_to_space')}
          </button>
        </div>
      );
    }

    return (
      <div className="cards-grid">
        {allInvites.map(inv => {
          // All four space types and their canonical colors live in lib/space-types.js
          const { label: typeLabel, color: typeColor } = getSpaceType(inv.space_type);
          const isReceived = inv.direction === 'received';

          if (isReceived) return (
            <ShiftCard
              key={inv.id}
              item={{
                id: inv.id,
                title: inv.inviterName || 'Someone',
                created_at: inv.created_at,
                summary_bullets: inv.personal_note ? [inv.personal_note] : [],
              }}
              accentColor={typeColor}
              typeLabel={typeLabel}
              preview={inv.spaceName || 'Invited you to a space'}
              actionLabel="Accept →"
              onAction={() => acceptSharedInvite(inv.id)}
              onDelete={() => declineSharedInvite(inv.id)}
            />
          );

          return (
            <ShiftCard
              key={inv.id}
              item={{
                id: inv.id,
                title: inv.invitee_email,
                created_at: inv.created_at,
                summary_bullets: inv.personal_note ? [inv.personal_note] : [],
              }}
              accentColor={typeColor}
              typeLabel={`${typeLabel} · Pending`}
              preview={inv.spaceName || 'Waiting for response'}
              onDelete={() => withdrawInvite(inv.id)}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="shift-panel-overlay">
      <ShiftPanelHeader
        title="Space"
        tagline="Build, learn, create — solo or with anyone."
        tabs={[
          { label: 'Sessions', color: '#c8a96e' },
          { label: 'Invites',  color: '#c8a96e' },
        ]}
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (tab === 'Invites') loadSentInvites();
        }}
        actionButtons={[
          { label: 'Solo',     onClick: onSolo },
          { label: '+ Invite', onClick: onInvite },
        ]}
        onClose={onClose}
      />

      <div className="panel-content">
        <SpaceToolbar activeTab={activeTab} />

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && activeTab === 'Sessions' && pendingInvites?.length > 0 && (
            <div style={{
              marginBottom: '20px', padding: '12px 16px',
              background: 'var(--purple-bg)',
              border: '1px solid var(--purple-border)',
              borderRadius: 'var(--radius-md)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ color: 'var(--purple)', fontSize: '12px', fontFamily: 'var(--font-serif)' }}>
                {pendingInvites.length} pending invite{pendingInvites.length > 1 ? 's' : ''} waiting for you
              </div>
              <button
                onClick={() => setActiveTab('Invites')}
                style={{ background: 'none', border: 'none', color: 'var(--purple)', fontSize: '11px', fontFamily: 'var(--font-serif)', cursor: 'pointer', letterSpacing: '0.06em', padding: 0 }}
              >
                View →
              </button>
            </div>
          )}
          {activeTab === 'Sessions' && <SessionsTab />}
          {activeTab === 'Invites'  && <InvitesTab />}
        </div>
      </div>

      {/* Confirmation dialog */}
      {confirmAction && (
        <DeleteSpaceConfirm
          spaceId={confirmAction.spaceId}
          spaceName={confirmAction.spaceName}
          isCreator={confirmAction.isCreator}
          supabase={spaceHook.supabase}
          onCancel={() => setConfirmAction(null)}
          onConfirm={async () => {
            if (confirmAction.isCreator) {
              await deleteSpaceSession(confirmAction.spaceId);
            } else {
              await leaveSpaceSession(confirmAction.spaceId);
            }
            setConfirmAction(null);
          }}
        />
      )}
    </div>
  );
}
